import { useState, useEffect, useRef, useCallback } from 'react';

const SIZE_MAP = { sm: 48, md: 72, lg: 96 };
const STROKE_WIDTH = 4;

/**
 * Server-synced countdown timer with circular SVG ring.
 * Colour shifts green → amber → red based on remaining time.
 */
export default function GameTimer({ timerEndsAt, totalDuration, onExpire, size = 'md' }) {
  const [remaining, setRemaining] = useState(totalDuration);
  const expiredRef = useRef(false);
  const rafRef = useRef(null);

  const tick = useCallback(() => {
    if (!timerEndsAt) return;
    const now = Date.now();
    const ms = Math.max(0, timerEndsAt - now);
    const secs = Math.ceil(ms / 1000);
    setRemaining(secs);

    if (ms <= 0 && !expiredRef.current) {
      expiredRef.current = true;
      onExpire?.();
      return;
    }

    if (ms > 0) {
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [timerEndsAt, onExpire]);

  useEffect(() => {
    expiredRef.current = false;
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [tick]);

  const fraction = totalDuration > 0 ? remaining / totalDuration : 0;
  const diameter = SIZE_MAP[size] || SIZE_MAP.md;
  const radius = (diameter - STROKE_WIDTH) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - fraction);

  // Colour shift: green > amber at 20% > red at 10%
  let colour = '#22C55E'; // green
  if (fraction <= 0.2) colour = '#EF4444'; // red
  else if (fraction <= 0.35) colour = '#F59E0B'; // amber

  const displayTime = remaining >= 60
    ? `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`
    : String(remaining);

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: diameter, height: diameter }}>
      <svg width={diameter} height={diameter} className="-rotate-90">
        {/* Background ring */}
        <circle
          cx={diameter / 2}
          cy={diameter / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={STROKE_WIDTH}
          className="text-slate-200"
        />
        {/* Progress ring */}
        <circle
          cx={diameter / 2}
          cy={diameter / 2}
          r={radius}
          fill="none"
          stroke={colour}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{ transition: 'stroke 0.3s ease' }}
        />
      </svg>
      <span
        className="absolute font-mono font-bold"
        style={{
          fontSize: size === 'sm' ? 14 : size === 'lg' ? 24 : 18,
          color: colour,
        }}
      >
        {displayTime}
      </span>
    </div>
  );
}
